import {
  useCallback, useContext, useEffect, useState
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Stack } from "@fluentui/react/lib/Stack";
import { Pivot, PivotItem } from "@fluentui/react";

import { CustomPanel, CustomPanelSizeType } from "Components/Generic/CustomPanel";
import { IWithThemeProps } from "Components/Generic/Theme/IWithThemeProps";
import { WithTheme } from "Components/Generic/Theme/WithTheme";
import { Toolbar } from "Components/Toolbar";
import { uiSetActiveCompanyId, uiSetActiveContactId, uiSetDetailsOpen } from "Store/Actions";
import { selectActiveItem, selectActiveItemType } from "Store/Selectors/Projects";
import { selectActiveCompanyId, selectActiveContactId, selectIsDetailsOpen } from "Store/Selectors/UI";
import { isNotNullOrWhiteSpace } from "Utils/String";
import { TranslationContext } from "Components/Generic/Translator";
import { Footer } from "./Footer";
import { HeaderTitle } from "./HeaderTitle";
import { Info } from "./Info";
import { Participants } from "./Participants";
import { SubHeader } from "./SubHeader";
import { useDetailsStyles } from "./UseDetailsStyles";

export const Details = WithTheme(({ theme: { spacing } }: IWithThemeProps): JSX.Element => {
  const translator = useContext(TranslationContext);
  const dispatch = useDispatch();
  const styles = useDetailsStyles();
  const isDetailsOpen = useSelector(selectIsDetailsOpen);
  const selectedCompanyId = useSelector(selectActiveCompanyId);
  const selectedContactId = useSelector(selectActiveContactId);
  const activeItemType = useSelector(selectActiveItemType);
  const activeItem = useSelector(selectActiveItem);

  const [selectedKey, setSelectedKey] = useState(`firstTab`);

  useEffect(() => {
    setSelectedKey(activeItemType === `Company` && `firstTab`);
  }, [activeItemType]);

  const onDismiss = useCallback(() => {
    if (isNotNullOrWhiteSpace(selectedContactId)) {
      dispatch(uiSetActiveContactId(null));
      return;
    }

    if (isNotNullOrWhiteSpace(selectedCompanyId)) {
      dispatch(uiSetActiveCompanyId(null));
      return;
    }

    dispatch(uiSetDetailsOpen(false));
  }, [dispatch, selectedCompanyId, selectedContactId]);

  if (!isDetailsOpen) {
    return null;
  }

  // eslint-disable-next-line no-nested-ternary
  const detailsTab = activeItemType === `Project`
    ? translator.get(`details.project.details`, `Details`)
    : activeItemType === `Company`
      ? translator.get(`details.company.details`, `Company-Details`)
      : translator.get(`details.contact.details`, `Contact-Details`);
  const participantsTab = activeItemType === `Project`
    ? translator.get(`details.project.companies`, `Companies`)
    : translator.get(`details.company.contacts`, `Contacts`);

  function handleLinkClick(item?: PivotItem) {
    if (item) setSelectedKey(item.props.itemKey);
  }

  return (
    <CustomPanel
      customPanelSize={CustomPanelSizeType.large}
      isOpen={isDetailsOpen}
      headerTitle={<HeaderTitle />}
      onDismiss={onDismiss}
      isBlocking
      isLightDismiss
    >
      <Stack className={styles.root} tokens={{ childrenGap: spacing.s1 }}>
        <Stack.Item>
          <SubHeader />
        </Stack.Item>
        <Stack.Item className={styles.toolbar} align="end">
          <Toolbar type={activeItemType} />
        </Stack.Item>

        <Stack.Item className={styles.content} grow>
          <Pivot className={styles.pivot} selectedKey={selectedKey} onLinkClick={(item) => handleLinkClick(item)}>
            <PivotItem className={styles.pivotItem} itemKey="firstTab" headerText={detailsTab}>
              <Stack.Item disableShrink className={styles.details}>
                <Info item={activeItem} type={activeItemType} />
              </Stack.Item>
            </PivotItem>
            {activeItemType !== `Contact` && (
              <PivotItem className={styles.pivotItem} itemKey="secondTab" headerText={participantsTab}>
                <Stack className={styles.participants} grow>
                  <Participants />
                </Stack>
              </PivotItem>
            )}
          </Pivot>
        </Stack.Item>

        <Stack horizontal className={styles.footer} tokens={{ childrenGap: spacing.m }}>
          <Footer />
        </Stack>
      </Stack>
    </CustomPanel>
  );
});
